import type { SubscriptionScope, SubscriptionType, SubscriptionTypeConfig, TriggerType } from "./Subscriptions";

export enum NotificationChannelType {
    IN_APP = 'In App',
    MOBILE_APP = 'Mobile App',
    EMAIL = 'Email',
    WEBHOOK = 'Webhook',
    DISCORD = 'Discord',
    TELEGRAM = 'Telegram',
}

export interface NotificationChannel {
    type: NotificationChannelType;
    settings?: {
        telegramChatId?: number;
        webhookUrl?: string;
        discordWebhookUrl?: string;
    };
    status?: 'PENDING' | 'SENT' | 'FAILED' | 'READ';
    error?: string; // Error message if status is 'FAILED'
}

export type NotificationType = 'price_notification' | 'event_status_notification';

export interface PriceNotificationSchema {
    type: NotificationType;

    userId: string;
    createdAt: Date; // Using Date here for MongoDB compatibility and easy sorting

    subscriptionId: string;

    watchlistId: string;
    watchlistName: string;

    eventId: string;
    eventTitle: string;

    marketId: string;
    marketQuestion: string;

    polymarketSlug: string;

    clobTokenId: string;
   
    subscriptionScope: SubscriptionScope;
    subscriptionTypeConfig: SubscriptionTypeConfig;
    
    triggerType: TriggerType;
    detectedValue: number;
    
    // Timing information
    sentFromPolymarketAtMs: number; // When the notification was sent from Polymarket. Unix timestamp in milliseconds
    detectedAt: string;               // When the condition was detected. ISO 8601
    detectedAtMs: number;           // When the condition was detected. Unix timestamp in milliseconds
    sentToUserAtMs?: number;             // When notification was sent. ISO 8601
    readFromUserAtMs?: number;          // When user read the notification. Unix timestamp in milliseconds
    
    // Content
    title?: string;           // E.g., "Price Spike Alert 🚀"
    message?: string;         // E.g., "Market XYZ has increased by 20% in the last hour"
    
    channels: NotificationChannel[];

    polymarketSourceEvent?: any;
}

export enum EventStatusScope {
    MARKET = 'Market',
    EVENT = 'Event',
}

export enum EventStatusTriggerType {
    OPEN = 'Open',
    CLOSED = 'Closed',
}

export interface EventStatusNotificationSchema {
    type: NotificationType;

    userId: string;
    createdAt: Date; // Using Date here for MongoDB compatibility and easy sorting
    subscriptionId: string;

    watchlistId: string;
    watchlistName: string;

    eventId: string;
    eventTitle: string;

    marketId?: string;
    marketQuestion?: string;
    outcomes?: string;
    outcomePrices?: string;

    polymarketSlug: string;

    eventStatusScope: EventStatusScope;
    triggerType: EventStatusTriggerType;

    channels: NotificationChannel[];

    detectedAt: string; // ISO 8601
    detectedAtMs: number; // Unix timestamp in milliseconds
    sentToUserAtMs?: number; // Unix timestamp in milliseconds
}

export type NotificationSchema = PriceNotificationSchema | EventStatusNotificationSchema;

export interface PriceNotificationResponse extends Omit<PriceNotificationSchema, 'createdAt'> {
    id: string;
    createdAt: string;  // ISO 8601 , we need this conversion to be able to send the response as string
}

export interface EventStatusNotificationResponse extends Omit<EventStatusNotificationSchema, 'createdAt'> {
    id: string;
    createdAt: string;  // ISO 8601 , we need this conversion to be able to send the response as string
}

export type NotificationResponse = PriceNotificationResponse | EventStatusNotificationResponse;

export interface GetNotificationsJSONRequest {
    limit?: number;
    before?: string; // ISO timestamp
}

export interface GetNotificationsJSONResponse {
    notifications: NotificationResponse[];
    hasMore: boolean;
    nextCursor: string | null;
}

export interface UpdateNotificationsJSONRequest {
    notificationIds: string[];
}

export interface UpdateNotificationsJSONResponse {
    notificationsUpdated: number;
}

export interface DeleteNotificationsJSONRequest {
    action: 'delete';
    notificationIds: string[];
}

export interface DeleteNotificationsJSONResponse {
    notificationsDeleted: number;
}